"use client";

import { useIsOnline } from "@/common/hooks/use-is-online";
import { useLogger } from "@/logging/client/LoggerContext";
import { pingServer } from "@/setup/frontend/ping-server";
import { useCacheStorage } from "@/setup/frontend/storage/CacheStorageContext";
import { usePreferenceStorage } from "@/setup/frontend/storage/PreferenceStorageContext";
import { getCurrentUser, signOut } from "aws-amplify/auth";
import { rateLimitedFetchAuthSession } from "@/setup/frontend/fetchAuthSession";
import { useEffect } from "react";
import { useIdleTimer } from "react-idle-timer";

const localAuthModule =
	import.meta.env.VITE_CAPACITOR_BUILD === "true"
		? import("@vcg/capacitor-local-auth")
		: null;

const ACTION_THROTTLE_MS = 5000;
const IDLE_TIME_MS = 1000 * 60 * 30; // 30 minutes
const DEFAULT_MAX_TIME_SINCE_LAST_LOGIN_MS = 1000 * 60 * 60 * 12; // 12 hours
const EXTENDED_MAX_TIME_SINCE_LAST_LOGIN_MS = 1000 * 60 * 60 * 24 * 14; // 14 days

// this is not a security mechanism. Its purpose is to improve the UX of session expiration.
export default function IdleSessionHandler() {
	const preferenceStorage = usePreferenceStorage();
	const isOnline = useIsOnline();
	const cacheStorage = useCacheStorage();
	const logger = useLogger();
	useEffect(() => {
		async function checkIdleSession() {
			const lastActiveTime = await preferenceStorage
				.getItem("vcg-crm-last-active-time")
				.catch(() => null);
			if (lastActiveTime) {
				const lastActiveTimeDate = new Date(lastActiveTime);
				const timeSinceLastActive =
					new Date().getTime() - lastActiveTimeDate.getTime();
				const isIdle = timeSinceLastActive > IDLE_TIME_MS;
				if (isIdle) {
					logger.info(
						`IdleSessionHandler: user idle - last active at ${lastActiveTimeDate}`,
					);
					// Determine max time based on conditions
					let maxTimeSinceLastLogin =
						import.meta.env.VITE_CAPACITOR_BUILD === "true"
							? EXTENDED_MAX_TIME_SINCE_LAST_LOGIN_MS
							: DEFAULT_MAX_TIME_SINCE_LAST_LOGIN_MS;

					const lastLoginTime = await rateLimitedFetchAuthSession({
						skipCache: true,
					})
						.then((session) => {
							return session.tokens?.accessToken.payload.auth_time as
								| number
								| undefined;
						})
						.catch(() => null);

					const timeSinceLastLogin = lastLoginTime
						? new Date().getTime() - lastLoginTime * 1000
						: null;
					logger.info(
						`IdleSessionHandler: user idle - last login at ${lastLoginTime}`,
					);

					// some users do not have or have not enrolled in local auth.
					// for security purposes, we want to force them to re-authenticate with the server more regularly.
					// However, for UX sake, we do not want to force customer accounts (e.g. clients or trusted contacts) to login this frequently.
					// Customer accounts use passwordless auth methods (e.g. email), so this logic checks if the user signed in with a password.
					if (
						import.meta.env.VITE_CAPACITOR_BUILD === "true" &&
						localAuthModule &&
						timeSinceLastLogin &&
						timeSinceLastLogin > DEFAULT_MAX_TIME_SINCE_LAST_LOGIN_MS // no point to checking while below threshold
					) {
						const { LocalAuth } = await localAuthModule;
						const availability = await LocalAuth.checkAvailability().catch(
							() => ({
								// If there's an error checking local auth, assume it's not available
								available: false,
							}),
						);

						if (!availability.available) {
							// Local auth is NOT available, check authFlowType
							const user = await getCurrentUser();
							const authFlowType = user.signInDetails?.authFlowType;

							// check if user signed in with password
							if (
								authFlowType === "USER_AUTH" ||
								authFlowType === "USER_SRP_AUTH" ||
								authFlowType === "USER_PASSWORD_AUTH"
							) {
								maxTimeSinceLastLogin = DEFAULT_MAX_TIME_SINCE_LAST_LOGIN_MS;
								logger.info(
									`IdleSessionHandler: user idle - no local auth -  auth flow type ${authFlowType} - setting max time since last login ${maxTimeSinceLastLogin}`,
								);
							}
						}
					}

					if (
						timeSinceLastLogin &&
						timeSinceLastLogin > maxTimeSinceLastLogin
					) {
						const canReachServer = await pingServer(10);
						// if can't reach server in a reasonable amount of time, we can skip the signout.
						// this is to avoid a situation were the user is signed out but unable to login because
						// they're unable to connect to the server due to network issues.
						if (!canReachServer) {
							logger.info(
								`IdleSessionHandler: user idle - time since last login ${timeSinceLastLogin} exceeds max - cannot reach server`,
							);
							return;
						}

						logger.info(
							`IdleSessionHandler: user idle - time since last login ${timeSinceLastLogin} exceeds max - signing out`,
						);

						await logger.flush().catch((e) => console.error(e));

						// sign out to force re-authentication and get a new refresh token.
						// we intentionally do not clear cache or preferences. hwoever, we do clear
						// the current user cache otherwise the signout will only be partial.

						cacheStorage.removeItem("vcg-current-user").catch(() => null);

						signOut()
							.then(() => {
								window.location.reload();
							})
							.catch(() => {
								// ignore errors
							});
					}
				}
			}
		}

		if (!isOnline) {
			// if offline, there is no way to re-authenticate. So we don't need to check for idle sessions.
			return;
		}

		checkIdleSession();
	}, [isOnline]);

	useIdleTimer({
		onAction: () => {
			preferenceStorage
				.setItem("vcg-crm-last-active-time", new Date().toISOString())
				.catch((e) => {
					console.error(e);
				});
		},
		throttle: ACTION_THROTTLE_MS,
	});

	return null;
}
